import React, { useEffect, useState } from "react";
import MuiPaper from "Components/MuiPaper/MuiPaper";
import Translator from "Languages/Utility/Translator";
import { satc_priceProduct } from "Constants/TableColumns";
import RemoteTable from "Components/RemoteTable/RemoteTable";
import { useTranslation } from "react-i18next";
import { useSetState } from "Helpers/Hooks";
import MuiAutoComplete from "Components/MuiAutoComplete/MuiAutoComplete";
import Grid from "@material-ui/core/Grid";
import { read } from "Helpers/LocalStorage";
import { Skeleton } from "@material-ui/lab";
import { useDispatch, useSelector } from "react-redux";
import {
  getAccountingPriceProduct,
  getActivePeriods,
  getProductCombo,
} from "Stores/actions";
import BackDropLoading from "Components/BackDropLoading2/BackDropLoading";
import AddPriceProducts from "./AddPriceProducts";
import PageTitle from "Components/PageTitle_IconCircle/PageTitle";

export default function Products() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [sizePerPage, setSizePerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [tblData, setTblData] = useState();
  const [columns, setColumns] = useState([]);
  const {
    accountPriceProduct,
    activePeriods,
    productCombo,
    getPriceProductAgain,
    pagination,
  } = useSelector((state) => ({
    accountPriceProduct: state.saAccounts.accountPriceProduct,
    activePeriods: state.saAccounts.activePeriods,
    productCombo: state.saAccounts.productCombo,
    getPriceProductAgain: state.saAccounts.getPriceProductAgain,
    pagination: state.saAccounts.pagination,
  }));
  const [controls, setControls] = useSetState({
    periods: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
    },
    products: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
    },
  });

  useEffect(() => {
    onEnter();
  }, []);

  const onEnter = async () => {
    setLoading(true);
    const _columns = [...satc_priceProduct];
    _columns[10].hidden = !read("FormPriceSuperAdmin").accessUpdate;
    _columns[11].hidden = !read("FormPriceSuperAdmin").accessDelete;
    setColumns(_columns);
    await getPeriods();
    await getProductCombos();
    await getPrices(1, sizePerPage);
  };

  const getPrices = async (_page, _sizePerPage, dataSearch = null) => {
    setLoading(true);
    return new Promise(async (resolve) => {
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
        Limit: _sizePerPage,
        Page: _page,
        ...dataSearch,
      };
      await dispatch(getAccountingPriceProduct(params));
      setLoading(false);
      resolve(true);
    });
  };

  const getPeriods = async () => {
    return new Promise(async (resolve) => {
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
      };
      await dispatch(getActivePeriods(params));
      resolve(true);
    });
  };

  const getProductCombos = async () => {
    return new Promise(async (resolve) => {
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
      };
      await dispatch(getProductCombo(params));
      resolve(true);
    });
  };

  useEffect(() => {
    if (accountPriceProduct) {
      setTblData(accountPriceProduct);
      setSizePerPage(pagination.Limit);
      setCurrentPage(pagination.Page);
      setTotal(pagination.Total);
    }
  }, [accountPriceProduct]);

  useEffect(() => {
    if (activePeriods) {
      const defArr = [{ label: t("chooseOnePeriod"), value: -1 }];
      const selectItems = activePeriods.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({ periods: { selectItems: defArr.concat(selectItems) } });
      setControls({ periods: { value: defArr[0] } });
    }
  }, [activePeriods]);

  useEffect(() => {
    if (productCombo) {
      const defArr = [{ label: t("chooseOneProduct"), value: -1 }];
      const selectItems = productCombo.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({ products: { selectItems: defArr.concat(selectItems) } });
      setControls({ products: { value: defArr[0] } });
    }
  }, [productCombo]);

  const onTableChange = async (type, { page, sizePerPage }) => {
    if (type === "pagination") {
      await getPrices(page, sizePerPage);
    }
  };

  const autoCompleteHandler = async (e, value) => {
    const controlName = e.target.id.split("-")[0];
    setControls({ [controlName]: { value: value } });
    await tblRefresh(
      1,
      sizePerPage,
      controlName === "products" ? value.value : null,
      controlName === "periods" ? value.value : null
    );
  };

  useEffect(() => {
    if (getPriceProductAgain) {
      (async () => {
        await tblRefresh(currentPage, sizePerPage);
      })();
    }
  }, [getPriceProductAgain]);

  const tblRefresh = async (
    innerPage,
    innerSizePerPage,
    product = null,
    period = null
  ) => {
    let dataSearch = {};
    const { products, periods } = controls;
    if (product) {
      if (product !== -1) dataSearch.ProductId = product;
      if (periods.value.value !== -1) dataSearch.PeriodId = periods.value.value;
    } else if (period) {
      if (period !== -1) dataSearch.PeriodId = period;
      if (products.value.value !== -1)
        dataSearch.ProductId = products.value.value;
    } else {
      if (products.value.value !== -1)
        dataSearch.ProductId = products.value.value;
      if (periods.value.value !== -1) dataSearch.PeriodId = periods.value.value;
    }
    await getPrices(innerPage, innerSizePerPage, dataSearch);
  };

  return (
    <>
      <BackDropLoading loading={loading} />
      <div className="page-top-title-button my-3">
        <PageTitle title={<Translator id={"products"} />} />
        <div className="top-buttons">
          <AddPriceProducts disabled={loading} />
        </div>
      </div>
      <MuiPaper>
        <Grid container spacing={2} className="mb-2">
          {Object.keys(controls).map((key) => {
            return (
              <>
                {controls[key].type === "select" && (
                  <Grid item md={3} sm={6} xs={12}>
                    {!loading ? (
                      <MuiAutoComplete
                        autoSelect={true}
                        options={controls[key].selectItems}
                        label={<Translator id={key} />}
                        value={controls[key].value}
                        onChange={autoCompleteHandler}
                        id={key}
                      />
                    ) : (
                      <Skeleton
                        variant="text"
                        className="p-2"
                        animation="wave"
                      />
                    )}
                  </Grid>
                )}
              </>
            );
          })}
        </Grid>
        {columns && columns.length !== 0 && (
          <RemoteTable
            data={tblData}
            columns={columns}
            page={currentPage}
            sizePerPage={sizePerPage}
            totalSize={total}
            onTableChange={onTableChange}
          />
        )}
      </MuiPaper>
    </>
  );
}
